import { redirect } from "@tanstack/react-router"
import { ensureSession, type SessionUser } from "@/core/auth"
import { backendUrl } from "@/core/backend-url"
import { authApiPath } from "@/core/api-contract"

// Server-side session check: read the incoming request's cookie (the BA
// cross-subdomain *.attend.vip cookie is sent to this origin) and validate it
// against the backend. Runs only in the SSR bundle; the dynamic import keeps
// `@tanstack/react-start/server` out of the client build. Returns the user so
// callers can seed it into the query cache for flash-free SSR of the avatar.
async function sessionUserOnServer(): Promise<SessionUser | null> {
  try {
    const { getRequestHeader } = await import("@tanstack/react-start/server")
    const cookie = getRequestHeader("cookie") ?? ""
    if (!cookie) return null
    const res = await fetch(backendUrl(authApiPath("/get-session")), {
      headers: { cookie, accept: "application/json" },
    })
    if (!res.ok) return null
    const body = (await res.json().catch(() => null)) as { user?: SessionUser | null } | null
    return body?.user ?? null
  } catch {
    return null
  }
}

// Route guard for `beforeLoad`. Redirects to /sign-in (preserving the target as
// ?next=) when there's no session. On the server this issues a 302 before the
// page renders (no flash); on the client it uses the cached session check.
// Returns the resolved user so `beforeLoad` can seed ["session", "me"] into the
// query cache — on SSR that dehydrates → hydrates, so the avatar/profile render
// correct in the server HTML instead of flashing in after mount.
export async function requireSession(href: string): Promise<SessionUser> {
  const user = import.meta.env.SSR
    ? await sessionUserOnServer()
    : await ensureSession()
  if (!user) {
    throw redirect({ to: "/sign-in", search: { next: href } })
  }
  return user
}
